import React from 'react';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { Smartphone, Stethoscope, TrendingUp, Users, Bot, Settings, Cog } from 'lucide-react';

export default function ServicesSection() {
  const { t } = useTranslation();

  const services = [
    { key: 'mobile', icon: Smartphone, color: 'text-sky-500' },
    { key: 'medical', icon: Stethoscope, color: 'text-emerald-500' },
    { key: 'finance', icon: TrendingUp, color: 'text-violet-500' },
    { key: 'automation', icon: Cog, color: 'text-sky-500' },
    { key: 'management', icon: Settings, color: 'text-emerald-500' },
    { key: 'ai', icon: Bot, color: 'text-violet-500' },
    { key: 'entrepreneurs', icon: Users, color: 'text-sky-500' },
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: { opacity: 1, transition: { staggerChildren: 0.08 } },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 25 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.4 } },
  };

  const getGridClass = (index) => {
    const patterns = [
      'md:col-span-7',
      'md:col-span-5',
      'md:col-span-4',
      'md:col-span-4',
      'md:col-span-4',
      'md:col-span-6',
      'md:col-span-6',
    ];
    return patterns[index] || 'md:col-span-4';
  };

  return (
    <section id="services" className="relative py-24 md:py-32" data-testid="services-section">
      <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="text-center mb-16 max-w-2xl mx-auto"
        >
          <span className="inline-block font-outfit text-[11px] uppercase tracking-[0.2em] text-zinc-500 font-semibold mb-4">
            {t('services.label')}
          </span>
          <h2 className="font-outfit font-medium text-3xl md:text-4xl lg:text-5xl tracking-tight text-zinc-950 mb-5">
            {t('services.title')}{' '}
            <span className="text-gradient">{t('services.titleHighlight')}</span>
          </h2>
          <p className="font-manrope text-base md:text-lg text-zinc-600 leading-relaxed">
            {t('services.subtitle')}
          </p>
        </motion.div>

        {/* Services Bento Grid */}
        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          className="grid grid-cols-1 md:grid-cols-12 gap-4 md:gap-5"
        >
          {services.map((service, index) => {
            const Icon = service.icon;
            return (
              <motion.div
                key={service.key}
                variants={itemVariants}
                className={`group relative ${getGridClass(index)} p-7 soft-card`}
                data-testid={`service-${service.key}`}
              >
                <div className="relative z-10">
                  <div className="w-12 h-12 rounded-xl icon-tile flex items-center justify-center mb-6 group-hover:scale-105 transition-transform duration-200">
                    <Icon className={`w-6 h-6 ${service.color}`} />
                  </div>

                  <h3 className="font-outfit font-medium text-lg md:text-xl mb-3 text-zinc-900">
                    {t(`services.items.${service.key}.title`)}
                  </h3>

                  <p className="font-manrope text-sm md:text-[15px] text-zinc-600 leading-relaxed">
                    {t(`services.items.${service.key}.description`)}
                  </p>
                </div>
              </motion.div>
            );
          })}
        </motion.div>
      </div>
    </section>
  );
}
